import { IMapper, MirrorMode }                from '@/Emulator';
import { IBus }                               from '@/Emulator/Bus/IBus';
import { deserializeMemory, serializeMemory } from '@/Emulator/Utility';

export class PPUBus implements IBus, ISerializable
{
    private readonly nametables: Uint8Array;
    private readonly palettes: Uint8Array;

    constructor(private readonly mapper: IMapper)
    {
        this.nametables = new Uint8Array(2048); // 2KB internal Video RAM.
        this.palettes   = new Uint8Array(32); // 32 bytes of palette RAM.
    }

    public serialize(): Record<string, any>
    {
        return {
            nametables: serializeMemory(this.nametables),
            palettes:   serializeMemory(this.palettes),
        };
    }

    public deserialize(data: Record<string, any>): void
    {
        this.nametables.set(deserializeMemory(data.nametables));
        this.palettes.set(deserializeMemory(data.palettes));
    }

    /**
     * @inheritDoc
     */
    public peek(address: number): number
    {
        address &= 0x3FFF; // Enforce 16 KB address space.

        // 1. Palette Tables (Character ROM)
        if (address >= 0x0000 && address <= 0x1FFF) {
            return this.mapper.peekPpu(address);
        }

        // 2. Nametables ($2000 - $3EFF) -> Internal PPU VRAM with Mirroring
        if (address <= 0x3EFF) {
            return this.nametables[this.getMirroredVramAddress(address)];
        }

        // 3. Palettes ($3F00 - $3FFF) -> Internal PPU Palette RAM
        if (address >= 0x3F00) {
            return this.palettes[this.getPaletteAddress(address)];
        }

        return 0;
    }

    /**
     * @inheritDoc
     */
    public read(address: number): number
    {
        address &= 0x3FFF; // Enforce 16 KB address space.

        // 1. Palette Tables (Character ROM)
        if (address >= 0x0000 && address <= 0x1FFF) {
            return this.mapper.readPpu(address);
        }

        // 2. Nametables ($2000 - $3EFF) -> Internal PPU VRAM with Mirroring
        if (address <= 0x3EFF) {
            return this.nametables[this.getMirroredVramAddress(address)];
        }

        // 3. Palettes ($3F00 - $3FFF) -> Internal PPU Palette RAM
        if (address >= 0x3F00) {
            return this.palettes[this.getPaletteAddress(address)];
        }

        return 0;
    }

    /**
     * @inheritDoc
     */
    public write(address: number, value: number): void
    {
        address &= 0x3FFF;
        value &= 0xFF;

        // 1. Pattern Tables ($0000 - $1FFF) -> Write to CHR (e.g. if CHR-RAM)
        if (address >= 0x0000 && address <= 0x1FFF) {
            this.mapper.writePpu(address, value);
            return;
        }

        // 2. Nametables ($2000 - $3EFF) -> Internal PPU VRAM
        if (address <= 0x3EFF) {
            this.nametables[this.getMirroredVramAddress(address)] = value;
            return;
        }

        // 3. Palettes ($3F00 - $3FFF) -> Internal PPU Palette RAM
        if (address >= 0x3F00) {
            this.palettes[this.getPaletteAddress(address)] = value;
            return;
        }
    }

    /**
     * Resolves a 4KB Nametables address space into the physical 2KB VRAM array
     * based on the cartridge's hardware mirroring layout.
     */
    private getMirroredVramAddress(address: number): number
    {
        // Normalize the address to the nametable space ($2000 - $2FFF)
        const normalized: number           = (address - 0x2000) & 0x0FFF;
        const activeMirrorMode: MirrorMode = this.mapper.mirrorMode;

        switch (activeMirrorMode) {
            case MirrorMode.Vertical:
                // Screen 0 & 2 map to first 1KB, Screen 1 & 3 map to second 1KB
                return normalized & 0x07FF;

            case MirrorMode.Horizontal:
                // Screen 0 & 1 map to first 1KB, Screen 2 & 3 map to second 1KB
                return ((normalized & 0x0800) >> 1) | (normalized & 0x03FF);

            case MirrorMode.FourScreen:
            default:
                // Fallback baseline wrapper
                return normalized & 0x07FF;
        }
    }

    /**
     * Handles palette mirroring quirks.
     *
     * Addresses $3F10, $3F14, $3F18, $3F1C are mirrors of background
     * backgrounds ($3F00, $3F04, $3F08, $3F0C)
     */
    private getPaletteAddress(address: number): number
    {
        let paletteIndex: number = address & 0x001F;

        // Mirror sprite transparent indexes back to bg transparent indexes.
        if ((paletteIndex & 0x0003) === 0 && (paletteIndex & 0x0010) !== 0) {
            paletteIndex &= 0x000F;
        }

        return paletteIndex;
    }
}
